/**
 * sheetDrag — the decision logic behind dragging the account sheet closed.
 *
 * The sheet has always drawn a grab handle and never responded to it: a control
 * that looks draggable and isn't is a UI lie, and it trains people to distrust
 * every other affordance on the screen.
 *
 * Kept pure (no React, no DOM) because the parts that decide behaviour — how far
 * is far enough, how fast is a flick, and when a drag must yield to scrolling —
 * are exactly the parts jsdom cannot demonstrate with real touches.
 */

/** Dragged more than this share of the sheet's height → dismiss on release. */
export const DISMISS_DISTANCE_RATIO = 0.25;

/**
 * A downward flick faster than this (px per ms) dismisses regardless of
 * distance — matching the platform feel, where a quick flick closes a sheet you
 * barely moved.
 */
export const DISMISS_VELOCITY = 0.5;

/** Ignore sub-pixel jitter so a tap is never mistaken for a drag. */
export const DRAG_START_SLOP = 4;

/**
 * A flick must travel at least this far to count.
 *
 * Velocity alone is not enough: a finger twitch of a few pixels resolves over a
 * millisecond or two, which computes to an enormous px/ms and would slam the
 * sheet shut on a jitter. Requiring real travel keeps "quick flick" meaning a
 * deliberate gesture, while still not demanding a long deliberate drag.
 */
export const FLICK_MIN_DISTANCE = 24;

/**
 * How far the sheet has actually moved, given a raw pointer delta.
 *
 * Clamped at zero: the sheet is anchored to the bottom of the screen, so
 * dragging UP must not lift it off its anchor and reveal a gap underneath.
 */
export const dragOffset = (dy) => (Number.isFinite(dy) && dy > 0 ? dy : 0);

/**
 * May a drag begin from this pointer-down?
 *
 * The admin menu is long enough to scroll, which collides with drag-to-dismiss:
 * if any downward movement dismissed the sheet, the menu could never be
 * scrolled. So a drag starts either from the dedicated handle/header — always
 * draggable, since that is what the handle is FOR — or from content that is
 * already scrolled to the top, where there is nothing above to scroll to.
 */
export const canStartDrag = ({ fromHandle = false, scrollTop = 0 } = {}) =>
  Boolean(fromHandle) || Number(scrollTop) <= 0;

/**
 * On release: does the sheet close, or spring back?
 *
 * Distance OR velocity — a deliberate long drag and a quick flick are both
 * clear intent, and requiring the sheet be dragged fully off-screen would feel
 * like work rather than a gesture.
 */
export function shouldDismiss({ dy = 0, sheetHeight = 0, velocity = 0 } = {}) {
  const travelled = dragOffset(dy);
  if (travelled <= 0) return false;
  if (velocity >= DISMISS_VELOCITY && travelled >= FLICK_MIN_DISTANCE) return true;
  if (!Number.isFinite(sheetHeight) || sheetHeight <= 0) return false;
  return travelled >= sheetHeight * DISMISS_DISTANCE_RATIO;
}

/**
 * Downward speed of the gesture, px/ms.
 *
 * Guards a zero/absent elapsed time — two pointer events in the same
 * millisecond would otherwise produce Infinity and dismiss on any twitch.
 */
export function dragVelocity({ dy = 0, elapsedMs = 0 } = {}) {
  const travelled = dragOffset(dy);
  if (!Number.isFinite(elapsedMs) || elapsedMs <= 0) return 0;
  return travelled / elapsedMs;
}
